import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit, Lesson, UnitWorkshop, UnitExam } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Layers,
  BookOpen,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Video,
  GraduationCap,
  Calendar,
  Clock
} from 'lucide-react'

interface UnitWithContent extends Omit<Unit, 'workshop' | 'exam'> {
  lessons: Lesson[]
  workshop: UnitWorkshop | null
  exam: UnitExam | null
}

export default function UnitsList() {
  const [units, setUnits] = useState<UnitWithContent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set())
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; unit: Unit | null }>({
    open: false,
    unit: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadUnits()
  }, [])

  const loadUnits = async () => {
    try {
      // Cargar unidades
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('*')
        .order('unit_number', { ascending: true })

      if (unitsError) throw unitsError

      // Cargar lecciones
      const { data: lessonsData } = await supabase
        .from('lessons')
        .select('*')
        .order('lesson_number', { ascending: true })

      // Cargar talleres
      const { data: workshopsData } = await supabase
        .from('unit_workshops')
        .select('*')

      // Cargar exámenes
      const { data: examsData } = await supabase
        .from('unit_exams')
        .select('*')

      // Mapear columnas BD (name_en/name_es) a lo que espera el frontend (title/description)
      const mappedUnits = (unitsData || []).map((u: any) => ({
        ...u,
        title: u.title || u.name_es || u.name_en || `Unidad ${u.unit_number}`,
        description: u.description || u.description_es || u.description_en || '',
        order_num: u.order_num ?? u.unit_number ?? 0,
      }))

      const mappedLessons = (lessonsData || []).map((l: any) => ({
        ...l,
        title: l.title || l.title_es || l.title_en || `Lección ${l.lesson_number}`,
        description: l.description || l.description_es || l.description_en || '',
        order_num: l.order_num ?? l.order_index ?? l.lesson_number ?? 0,
      }))

      // Combinar datos
      const unitsWithContent = mappedUnits.map((unit: Unit) => ({
        ...unit,
        lessons: mappedLessons.filter((l: Lesson) => l.unit_id === unit.id),
        workshop: (workshopsData || []).find((w: UnitWorkshop) => w.unit_id === unit.id) || null,
        exam: (examsData || []).find((e: UnitExam) => e.unit_id === unit.id) || null
      }))

      setUnits(unitsWithContent)

      // Expandir todas las unidades por defecto
      setExpandedUnits(new Set(unitsWithContent.map((u: Unit) => u.id)))
    } catch (error) {
      console.error('Error loading units:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleUnit = (unitId: string) => {
    const newExpanded = new Set(expandedUnits)
    if (newExpanded.has(unitId)) {
      newExpanded.delete(unitId)
    } else {
      newExpanded.add(unitId)
    }
    setExpandedUnits(newExpanded)
  }

  const handleDelete = async () => {
    if (!deleteModal.unit) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('units')
        .delete()
        .eq('id', deleteModal.unit.id)

      if (error) throw error

      setUnits(units.filter(u => u.id !== deleteModal.unit?.id))
      setDeleteModal({ open: false, unit: null })
    } catch (error) {
      console.error('Error deleting unit:', error)
      alert('Error al eliminar la unidad. Asegúrate de que no tenga contenido asociado.')
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredUnits = units.filter(unit => {
    const search = searchTerm.toLowerCase()
    return (unit.title || '').toLowerCase().includes(search) ||
      (unit.description || '').toLowerCase().includes(search)
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unidades</h1>
          <p className="text-gray-600">Gestiona las unidades del curso con sus lecciones, talleres y exámenes</p>
        </div>
        <Link
          to="/admin/units/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Unidad
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar unidades..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
        />
      </div>

      {/* Units List */}
      <div className="space-y-4">
        {filteredUnits.length > 0 ? (
          filteredUnits.map((unit) => (
            <div key={unit.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Unit Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                role="button"
                tabIndex={0}
                onClick={() => toggleUnit(unit.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleUnit(unit.id) }}
              >
                <div className="flex items-center gap-4">
                  <button className="p-1">
                    {expandedUnits.has(unit.id) ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0055A4]/10 text-[#0055A4] font-bold text-lg">
                    {unit.order_num}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{unit.title}</h3>
                    <p className="text-sm text-gray-500">{unit.description}</p>
                  </div>
                </div>
                <span className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    unit.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {unit.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                  <Link
                    to={`/admin/units/${unit.id}/edit`}
                    className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => setDeleteModal({ open: true, unit })}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </span>
              </div>

              {/* Unit Content */}
              {expandedUnits.has(unit.id) && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="space-y-2">
                    {/* Lecciones */}
                    {unit.lessons.length > 0 ? (
                      unit.lessons.map((lesson) => (
                        <Link
                          key={lesson.id}
                          to={`/admin/lessons/${lesson.id}/edit`}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-[#0055A4]/5 transition-colors border"
                        >
                          <div className="p-2 rounded-lg bg-[#0055A4]/10">
                            <BookOpen className="w-4 h-4 text-[#0055A4]" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Lección {lesson.order_num}: {lesson.title}</p>
                            <p className="text-xs text-gray-500">{lesson.description || 'Sin descripción'}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            lesson.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {lesson.is_active ? 'Activa' : 'Inactiva'}
                          </span>
                        </Link>
                      ))
                    ) : (
                      <div className="p-3 bg-white rounded-lg border border-dashed text-center">
                        <p className="text-gray-500 text-sm">No hay lecciones</p>
                        <Link
                          to={`/admin/lessons/new?unit=${unit.id}`}
                          className="text-[#0055A4] hover:underline text-sm"
                        >
                          Crear lección
                        </Link>
                      </div>
                    )}

                    {/* Taller en vivo */}
                    {unit.workshop ? (
                      <Link
                        to={`/admin/workshops/${unit.workshop.id}/edit`}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-indigo-50 transition-colors border border-indigo-200"
                      >
                        <div className="p-2 rounded-lg bg-indigo-100">
                          <Video className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Taller en Vivo: {unit.workshop.title}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(unit.workshop.scheduled_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {unit.workshop.duration_minutes} min
                            </span>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          unit.workshop.is_active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {unit.workshop.is_active ? 'Programado' : 'Inactivo'}
                        </span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-dashed">
                        <div className="p-2 rounded-lg bg-gray-100">
                          <Video className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-500 text-sm">Taller en Vivo: No programado</p>
                        </div>
                        <Link
                          to={`/admin/workshops/new?unit=${unit.id}`}
                          className="text-indigo-600 hover:underline text-sm"
                        >
                          Programar
                        </Link>
                      </div>
                    )}

                    {/* Prueba semanal (Examen) */}
                    {unit.exam ? (
                      <Link
                        to={`/admin/exams/${unit.exam.id}/edit`}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-purple-50 transition-colors border border-purple-200"
                      >
                        <div className="p-2 rounded-lg bg-purple-100">
                          <GraduationCap className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Examen: {unit.exam.title}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Aprobar: {unit.exam.passing_score}%</span>
                            <span>{unit.exam.time_limit} min</span>
                            <span>{unit.exam.max_attempts} intentos</span>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          unit.exam.is_active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {unit.exam.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-dashed">
                        <div className="p-2 rounded-lg bg-gray-100">
                          <GraduationCap className="w-4 h-4 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-500 text-sm">Examen: No creado</p>
                        </div>
                        <Link
                          to={`/admin/exams/new?unit=${unit.id}`}
                          className="text-purple-600 hover:underline text-sm"
                        >
                          Crear examen
                        </Link>
                      </div>
                    )}

                    {/* Botón agregar lección */}
                    {unit.lessons.length < 4 && (
                      <Link
                        to={`/admin/lessons/new?unit=${unit.id}`}
                        className="flex items-center justify-center gap-2 p-3 bg-white rounded-lg border border-dashed text-[#0055A4] hover:bg-[#0055A4]/5 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm">Agregar Lección ({unit.lessons.length}/4)</span>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron unidades</p>
            <Link
              to="/admin/units/new"
              className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
            >
              Crear primera unidad
            </Link>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Unidad</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la unidad "{deleteModal.unit?.title}"?
              Esta acción no se puede deshacer y eliminará todas las lecciones, talleres y exámenes asociados.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, unit: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
